import React, { useCallback, useMemo } from 'react';

import { WDSButton, WDSModal } from 'wanderu-components';

import StationMapCore from './StationMapCore';
import createEvents from './events';

import styles from './StationMapSheet.scss';

import type { StationMapCoreProps } from './StationMapCore';
import type { TrackEvent } from './events';

interface StationMapSheetProps extends StationMapCoreProps {
    filteredTripCount: number;
    isOpen: boolean;
    onClose: () => void;
    trackEvent: TrackEvent;
}

function StationMapSheet({
    filteredTripCount,
    isOpen,
    onClose,
    trackEvent,
    ...coreProps
}: StationMapSheetProps) {
    const { searchParams, arriveStationCount, departStationCount } = coreProps;

    const sheetEvents = useMemo(() => createEvents(trackEvent), [trackEvent]);

    const baseProperties = useMemo(() => ({
        arrive_address: searchParams.destinationText,
        arrive_place_id: searchParams.destinationPlaceId,
        arrive_station_count: arriveStationCount,
        depart_address: searchParams.originText,
        depart_place_id: searchParams.originPlaceId,
        depart_station_count: departStationCount,
        device_form_factor: 'mobile' as const,
        funnel_stage: 'search' as const,
        is_one_way: !searchParams.returnDate,
        outbound_date: searchParams.departDate,
        return_date: searchParams.returnDate,
    }), [searchParams, arriveStationCount, departStationCount]);

    const handleClose = useCallback(() => {
        sheetEvents.mapClosed(baseProperties, 'mobile_sheet', 'bottom_sheet');
        onClose();
    }, [onClose, sheetEvents, baseProperties]);

    return (
        <WDSModal
            classes={{
                modalOverlay: styles.overlay,
                modal: styles.sheet,
                dialog: styles.dialog,
            }}
            isOpen={isOpen}
            onOpenChange={(open: boolean) => {
                if (!open) handleClose();
            }}
            title="Pick-up & drop-off"
            isDismissable
            showButton
        >
            <div className={styles.mapContainer}>
                <StationMapCore
                    {...coreProps}
                    trackEvent={trackEvent}
                />
            </div>

            <WDSButton
                className={styles.showTripsButton}
                onClick={handleClose}
                fullWidth
            >
                Show {filteredTripCount} trips
            </WDSButton>
        </WDSModal>
    );
}

export default React.memo(StationMapSheet);
